import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Users, ChevronRight, Loader2, Search, ExternalLink, ClipboardCheck,
  ShieldCheck, ShieldAlert, ShieldQuestion,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";

/**
 * Senior-management L2 hierarchy view for the /team page.
 *
 * Renders Department → Manager/Supervisor → Engineer rows with each
 * engineer's pay-period totals + a link to open their timesheet. Only shown
 * for users with role=admin OR job_title=Senior Manager.
 *
 * Props:
 *   periodNumber — the pay period to summarise (comes from the parent).
 */
export default function TeamHierarchyView({ periodNumber }) {
  const [data, setData] = useState({ groups: [], total_reports: 0, total_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [expandedDeps, setExpandedDeps] = useState(new Set());
  const [expandedLeaders, setExpandedLeaders] = useState(new Set());

  useEffect(() => {
    if (!periodNumber) return;
    let alive = true;
    setLoading(true);
    api.get(`/team/summary/hierarchy?period=${periodNumber}`)
      .then(({ data }) => {
        if (!alive) return;
        setData(data);
        // Expand all by default so admins can scan the whole tree.
        setExpandedDeps(new Set((data.groups || []).map((g) => g.department)));
        setExpandedLeaders(new Set(
          (data.groups || []).flatMap((g) =>
            g.leaders.map((l) => `${g.department}::${l.leader.id || "__unassigned__"}`)
          )
        ));
        setError(null);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "Failed to load hierarchy");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [periodNumber]);

  const toggleDep = (dep) => setExpandedDeps((s) => {
    const n = new Set(s); n.has(dep) ? n.delete(dep) : n.add(dep); return n;
  });
  const toggleLeader = (key) => setExpandedLeaders((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.groups || [];
    const groups = [];
    for (const g of data.groups || []) {
      const leaders = [];
      for (const l of g.leaders) {
        const reports = l.reports.filter((r) => {
          const u = r.user;
          return (
            (u.display_name || "").toLowerCase().includes(q) ||
            (u.employee_number || "").toString().toLowerCase().includes(q) ||
            (u.job_title || "").toLowerCase().includes(q) ||
            (l.leader.display_name || "").toLowerCase().includes(q) ||
            (g.department || "").toLowerCase().includes(q)
          );
        });
        if (reports.length) leaders.push({ ...l, reports, reports_count: reports.length });
      }
      if (leaders.length) groups.push({ ...g, leaders, reports_count: leaders.reduce((a, l) => a + l.reports_count, 0) });
    }
    return groups;
  }, [data.groups, query]);

  const approvalIcon = (row) => {
    const s = row.approval?.state;
    if (s === "L2_APPROVED") return { Icon: ShieldCheck, cls: "text-emerald-600", tip: "Fully approved" };
    if (s === "L1_APPROVED") return { Icon: ShieldAlert, cls: "text-amber-600", tip: "L1 approved · awaits L2" };
    if (s === "REJECTED") return { Icon: ShieldAlert, cls: "text-red-600", tip: "Rejected" };
    return { Icon: ShieldQuestion, cls: "text-gray-400", tip: "Not submitted" };
  };

  return (
    <div className="border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid="team-hierarchy-view">
      <div className="px-5 py-4 border-b border-summit-hairline flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Building2 className="w-3 h-3" /> Department · Manager · Engineer
          </div>
          <h2 className="font-display text-lg font-medium tracking-tight mt-0.5" data-testid="team-hierarchy-title">
            Reports hierarchy
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, emp #, manager…"
              className="h-9 pl-8 pr-3 border-summit-hairline text-xs w-72"
              data-testid="team-hierarchy-search"
            />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> {data.total_reports || 0} reports · {data.total_hours || 0} hrs
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-xs text-gray-400" data-testid="team-hierarchy-loading">
          <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading hierarchy…
        </div>
      ) : error ? (
        <div className="py-12 text-center text-xs text-red-600" data-testid="team-hierarchy-error">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400" data-testid="team-hierarchy-empty">
          No reports match the current filter.
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {filtered.map((dep) => {
            const depOpen = expandedDeps.has(dep.department);
            return (
              <div key={dep.department} className="border border-summit-hairline rounded-md bg-white" data-testid={`hierarchy-dep-${dep.department}`}>
                <div
                  onClick={() => toggleDep(dep.department)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleDep(dep.department); } }}
                  role="button" tabIndex={0} aria-expanded={depOpen}
                  className="w-full px-3 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-summit-glacier-soft/60"
                  data-testid={`hierarchy-dep-toggle-${dep.department}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${depOpen ? "rotate-90" : ""}`} />
                    <Building2 className="w-4 h-4 text-summit-primary" />
                    <span className="font-medium text-sm truncate">{dep.department}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-summit-hairline font-mono text-[10px] uppercase tracking-widest text-summit-ink bg-summit-glacier">
                      <Users className="w-3 h-3" /> {dep.reports_count}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 tabular-nums">
                      {dep.total_hours} h
                    </span>
                  </div>
                </div>
                {depOpen && (
                  <div className="border-t border-summit-hairline p-2 space-y-2">
                    {dep.leaders.map((l) => {
                      const leaderKey = `${dep.department}::${l.leader.id || "__unassigned__"}`;
                      const leaderOpen = expandedLeaders.has(leaderKey);
                      return (
                        <div key={leaderKey} className="border border-summit-hairline/70 rounded-md ml-3" data-testid={`hierarchy-leader-${leaderKey}`}>
                          <div
                            onClick={() => toggleLeader(leaderKey)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleLeader(leaderKey); } }}
                            role="button" tabIndex={0} aria-expanded={leaderOpen}
                            className="w-full px-3 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-summit-glacier-soft/40"
                            data-testid={`hierarchy-leader-toggle-${leaderKey}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${leaderOpen ? "rotate-90" : ""}`} />
                              <span className="text-sm font-medium truncate">
                                {l.leader.display_name}
                              </span>
                              {l.leader.job_title && (
                                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                                  · {l.leader.job_title}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{l.reports_count} report{l.reports_count === 1 ? "" : "s"}</span>
                              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 tabular-nums">{l.total_hours} h</span>
                            </div>
                          </div>
                          {leaderOpen && (
                            <div className="border-t border-summit-hairline/70">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-gray-500 bg-summit-canvas/60 border-b border-summit-hairline/70">
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Engineer</th>
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Emp #</th>
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Job title</th>
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Loc</th>
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Hours</th>
                                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Approval</th>
                                    <th className="px-3 py-2"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {l.reports.map((r) => {
                                    const { Icon, cls, tip } = approvalIcon(r);
                                    return (
                                      <tr key={r.user.id} className="border-b border-summit-hairline/70 last:border-0 hover:bg-summit-glacier-soft/30" data-testid={`hierarchy-row-${r.user.employee_number || r.user.id}`}>
                                        <td className="px-3 py-2 truncate max-w-[220px]">{r.user.display_name}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.user.employee_number || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{r.user.job_title || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.user.location || "—"}</td>
                                        <td className="px-3 py-2 font-mono tabular-nums">{r.total_hours || 0}</td>
                                        <td className="px-3 py-2">
                                          <span className="inline-flex items-center gap-1" title={tip}>
                                            <Icon className={`w-4 h-4 ${cls}`} />
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <Link
                                            to={`/timesheet?user_id=${r.user.id}&period=${periodNumber}`}
                                            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-summit-primary hover:underline"
                                            data-testid={`hierarchy-open-${r.user.employee_number || r.user.id}`}
                                          >
                                            <ClipboardCheck className="w-3 h-3" /> Timesheet
                                            <ExternalLink className="w-3 h-3" />
                                          </Link>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
